import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/lib/useSeo";

const articles = [
  {
    tag: "Data Security",
    title: "NIST 800-88 vs physical shredding: which does your data need?",
    excerpt: "A practical guide to choosing between cryptographic erasure, purge and destroy based on your data classification and compliance obligations.",
    read: "6 min read",
    date: "Dec 2025",
  },
  {
    tag: "Sustainability",
    title: "The real carbon cost of buying new IT (and how reuse beats it)",
    excerpt: "Manufacturing dominates a device's lifetime emissions. We break down why extending hardware life is the single biggest lever for IT sustainability.",
    read: "5 min read",
    date: "Nov 2025",
  },
  {
    tag: "Compliance",
    title: "What auditors actually look for in an ITAD paper trail",
    excerpt: "Serial-level chain of custody, downstream due diligence and certificates of destruction — the documents that turn a disposal into a defensible one.",
    read: "7 min read",
    date: "Nov 2025",
  },
  {
    tag: "Value Recovery",
    title: "Decommissioning a data center? Don't leave value in the rack.",
    excerpt: "How structured asset grading and remarketing can offset a significant portion of your decommissioning budget.",
    read: "8 min read",
    date: "Oct 2025",
  },
];

export default function Insights() {
  useSeo({
    title: "Insights — ITAD, Data Destruction & IT Sustainability | ENVIROTEK",
    description:
      "Practical insights on IT asset disposition, secure data destruction, e-waste recycling, compliance and IT sustainability from ENVIROTEK LLC FZ.",
    path: "/insights",
  });

  return (
    <div className="bg-background min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36" data-testid="insights-page">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">/ Insights</span>
              <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.86] mt-4">
                Field notes on<br />responsible IT retirement.
              </h1>
            </div>
            <p className="lg:col-span-4 font-sans text-muted-foreground leading-relaxed">
              Guidance for IT, security and sustainability leaders on getting asset disposition right.
            </p>
          </div>

          <div className="grid md:grid-cols-2 border-t border-l border-border mb-24">
            {articles.map((a, i) => (
              <motion.article
                key={a.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.08 }}
                data-testid={`insight-card-${i}`}
                className="group border-r border-b border-border p-8 md:p-12 hover:bg-foreground hover:text-background transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-widest border border-current px-2 py-1 text-primary">{a.tag}</span>
                  <span className="font-mono text-[11px] text-muted-foreground group-hover:text-background/50">{a.date} · {a.read}</span>
                </div>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight mb-4">{a.title}</h2>
                <p className="font-sans text-sm text-muted-foreground group-hover:text-background/70 leading-relaxed">{a.excerpt}</p>
                <ArrowUpRight size={20} className="mt-6 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.article>
            ))}
          </div>

          <div className="pb-24">
            <Link to="/#quote" data-testid="insights-cta" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-foreground transition-colors">
              Talk to our team <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
